import { Component } from "@theme/component";
import { ThemeEvents, QuantitySelectorUpdateEvent, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * How far above the viewport bottom the end of `<main>` triggers the bar to hide, in px. Roughly
 * the card's own footprint (height plus its bottom offset), so the exit starts just before the
 * footer would appear underneath it, while still leaving short mobile pages room to show it at all.
 */
const MAIN_END_LEAD_PX = 120;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "quantityDisplay", "quantityNumber"];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} True once `.buy-buttons-block` has scrolled above the viewport */
  #buyButtonsPassed = false;

  /** @type {boolean} True while the end of `<main>` is within the viewport (plus lead) */
  #mainEndInView = false;

  /** @type {MutationObserver | null} Re-asserts `data-stuck` when a morph overwrites it */
  #stuckAttributeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    // `bundle-picker.js` morphs the whole `<main>` from the fetched sibling page, which carries
    // `data-stuck="false"`. The morph keeps this node but rewrites the attribute, so the card
    // vanishes mid-page while `#isStuck` still says shown. Put the attribute back whenever it
    // stops matching the component's own state.
    this.#stuckAttributeObserver = new MutationObserver(() => this.#syncStuckAttribute());
    this.#stuckAttributeObserver.observe(this.refs.stickyBar, { attributes: true, attributeFilter: ["data-stuck"] });

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#stuckAttributeObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the two IntersectionObservers that drive visibility. The bar is shown only while
   * the buy buttons have scrolled above the viewport AND the end of `<main>` has not yet come
   * into view — see `#updateVisibility`.
   *
   * The page bottom is detected via the `[data-main-end]` marker that `layout/theme.liquid`
   * renders as the last child of `<main>`, rather than by observing `<footer>`: the theme's footer
   * is a `position: sticky` reveal that sits behind the main content, so geometrically it is inside
   * the viewport from the first frame and an observer on it never fires again. The end of the main
   * content is what actually marks the footer becoming visible.
   *
   * The marker is server-rendered on purpose. `bundle-picker.js` swaps sibling products by
   * morphing the whole `<main>` against the fetched page; a node this component appended itself
   * would have no counterpart there and be removed, leaving the observer watching a detached
   * element that can never report "in view".
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    const sentinel = document.querySelector("main [data-main-end]");
    if (!sentinel) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // "Passed" means above the viewport. Below (not yet reached) must not show the bar, so it
      // cannot flash on initial load.
      this.#buyButtonsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#updateVisibility();
    });

    // Hides the bar `MAIN_END_LEAD_PX` before the end of the main content reaches the bottom of
    // the viewport, so it has finished its exit transition by the time the footer is revealed.
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#mainEndInView = entry.isIntersecting;
        this.#updateVisibility();
      },
      {
        rootMargin: `0px 0px ${MAIN_END_LEAD_PX}px 0px`
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(sentinel);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Derives the bar's visibility from the two observed states. Order-independent: it does not
   * matter whether the buy buttons scroll past before or after the page end comes into view.
   */
  #updateVisibility() {
    const shouldShow = this.#buyButtonsPassed && !this.#mainEndInView;
    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  /**
   * Makes the DOM attribute agree with `#isStuck`. Writing only on mismatch keeps the
   * MutationObserver that calls this from re-triggering itself.
   */
  #syncStuckAttribute() {
    const { stickyBar } = this.refs;
    const expected = this.#isStuck ? "true" : "false";
    if (stickyBar.dataset.stuck !== expected) {
      stickyBar.dataset.stuck = expected;
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar by forwarding it to the real
   * product-form button. Feedback for the add is the cart drawer opening.
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {Event} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!(newStickyAddToCart instanceof HTMLElement)) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {Event} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    if (!(event instanceof VariantSelectedEvent)) return;
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles quantity selector update events
   * @param {Event} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
